// client/src/app/providers/ConfigProvider.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { validateRuntimeConfig } from "@/shared/configSchema";
import { listAllCustomValues } from "@/api";
import { getAuthToken } from "@/api/auth.js";

/** --- Constants --------------------------------------------------------- */

export const DEFAULT_API_CONFIG = {
  version: "2021-07-28",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

const STORAGE_KEY = "YG_CONFIG_v2";
const STORAGE_VERSION = 2;

// CV (custom values) → runtime config mapping (field *keys*, case-insensitive)
const CV_TO_CFG = {
  locationId: "locationid",
  calendarId: "calendarid",
  groupId: "groupid",
  calendarIdW: "calendaridw",
  pipelineId: "pipeline_id",
  pipelineStageId: "pipeline_stage_id",
  opportunityStatus: "opportunity_status",
  avgCover: "avg_cover",
  mealTimes: "meal_times",
  oppCFMap: "opp_cf_map",
  contactCFMap: "contactcfmap",
  featureFlags: "feature_flags",
  dynamicFields: "print_config",
  tableList: "table_list",
  pacingCaps: "pacing_caps",
  slotSizeMin: "slot_size_min",

  // Branding / CI
  brand_logo_url: "brand_logo_url",
  brand_primary_color: "brand_primary_color",
  brand_accent_color: "brand_accent_color",
  brand_font_heading: "brand_font_heading",
  brand_font_body: "brand_font_body",
  brand_theme_mode: "brand_theme_mode",

  // NEW: durations + form layout
  walkInDefaultDuration: "walkin_default_duration",
  newBookingDefaultDuration: "new_booking_default_duration",
  newBookingForm: "new_booking_form_config",
  newbookingModal: "new_booking_modal_config",
};

const URL_ALLOWLIST = new Set([
  "apiKey",
  "locationId",
  "calendarId",
  "calendarIdW",
  "groupId",
  "pipelineId",
  "pipelineStageId",
  "opportunityStatus",
  "avgCover",
  "tableList",
  "featureFlags",
  "contactCFMap", // allow URL override for testing

  // NEW: allow URL overrides (useful for testing)
  "walkin_default_duration",
  "new_booking_default_duration",
  "new_booking_form_config",
  "new_booking_modal_config",
]);

const DEFAULTS = {
  __v: STORAGE_VERSION,
  apiKey: "",
  locationId: "",
  calendarId: "",
  calendarIdW: "",
  groupId: "",
  pipelineId: "",
  pipelineStageId: "",
  opportunityStatus: "",
  avgCover: 0,
  mealTimes: {
    breakfast: { start: 8, end: 11 },
    lunch: { start: 11, end: 16 },
    dinner: { start: 16, end: 22 },
  },
  tableList: [], // may be legacy (array of strings) OR v2 object {version,rows}
  tableListV2: undefined, // when present, always {version:2, rows:[...]}
  slotSizeMin: 15,
  oppCFMap: {},
  contactCFMap: {}, // <-- default object
  dynamicFields: [],
  featureFlags: "reservations,month,settings", // sensible default

  // NEW: form + durations (and flags the UI can read)
  newBookingForm: undefined, // { version, fields } when present
  newbookingModal: undefined,
  walkInDefaultDuration: 90,
  newBookingDefaultDuration: 60,
  _usingDefaultBookingForm: false,
  _usingDefaultDurations: true,

  // Branding
  brand_logo_url: "",
  brand_primary_color: "",
  brand_accent_color: "",
  brand_font_heading: "",
  brand_font_body: "",
  brand_theme_mode: "default",

  timezone: "",
  _fetchedCV: false,
};

const Ctx = createContext(null);

const cvReloadInitial = 0;

/** --- Small helpers ----------------------------------------------------- */

function parseFeatureFlagsToSet(ffString) {
  return new Set(
    String(ffString || "")
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
  );
}

function parseQueryAllowlisted(search) {
  const params = new URLSearchParams(search);
  const o = {};
  for (const [k, v] of params.entries()) {
    if (URL_ALLOWLIST.has(k)) o[k] = v;
  }
  return o;
}

function coerceTypes(obj) {
  const out = { ...obj };
  if (typeof out.avgCover === "string" && out.avgCover.trim() !== "") {
    const n = Number(out.avgCover);
    if (!Number.isNaN(n)) out.avgCover = n;
  }
  // NEW: durations if passed via URL
  for (const k of ["walkin_default_duration", "new_booking_default_duration"]) {
    if (typeof out[k] === "string" && out[k].trim() !== "") {
      const n = parseInt(out[k].trim(), 10);
      if (Number.isFinite(n) && n > 0) {
        if (k === "walkin_default_duration") out.walkInDefaultDuration = n;
        if (k === "new_booking_default_duration")
          out.newBookingDefaultDuration = n;
        delete out[k];
      }
    }
  }
  // NEW: form config via URL (expects raw JSON string)
  if (
    typeof out.new_booking_form_config === "string" &&
    out.new_booking_form_config.trim() !== ""
  ) {
    try {
      const parsed = JSON.parse(out.new_booking_form_config);
      if (parsed && typeof parsed === "object") {
        out.newBookingForm = parsed;
        out._usingDefaultBookingForm = false;
      }
    } catch {}
    delete out.new_booking_form_config;
  }

  // NEW: contactCFMap via URL (expects JSON)
  for (const k of ["contactCFMap", "contactcfmap"]) {
    if (typeof out[k] === "string" && out[k].trim() !== "") {
      try {
        const parsed = JSON.parse(out[k]);
        if (parsed && typeof parsed === "object") out.contactCFMap = parsed;
      } catch {}
      delete out[k];
    }
  }

  return out;
}

function initialLocationId() {
  try {
    const u = new URL(window.location.href);
    const fromUrl =
      u.searchParams.get("locationId") || u.searchParams.get("loc");
    if (fromUrl) return fromUrl;
  } catch {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw);
      if (saved?.locationId) return saved.locationId;
    }
  } catch {}
  const envVal = import.meta.env.VITE_LOCATION_ID || "";
  if (envVal) return envVal;
  return "";
}

function normalizeCvKey(k = "") {
  // supports both raw ("avg_cover") and mustache ("{{ custom_values.avg_cover }}")
  return String(k)
    .trim()
    .toLowerCase()
    .replace(/^\{\{\s*custom_values\./, "")
    .replace(/\s*\}\}$/, "");
}

function toCvMap(cvResponse) {
  const out = Object.create(null);
  const list = cvResponse?.customValues || [];
  for (const cv of list) {
    const key = normalizeCvKey(cv.fieldKey || cv.key || "");
    const raw = cv.value ?? cv.fieldValueString ?? cv.fieldValue ?? "";
    out[key] = typeof raw === "string" ? raw.trim() : raw;
  }
  return out;
}

/** Robustly parse the table_list CV:
 * - v2 JSON string/object → {version:2, rows:[...]}
 * - legacy newline/CSV → array of strings (labels)
 */
function parseTableListCV(raw) {
  if (raw == null) return { tableList: [], tableListV2: undefined };

  // If already an object
  if (typeof raw === "object") {
    // Might be {version:2, rows:[...]} or even an array (legacy)
    if (raw.version === 2 && Array.isArray(raw.rows)) {
      return { tableList: raw, tableListV2: raw };
    }
    if (Array.isArray(raw)) {
      return {
        tableList: raw
          .map(String)
          .map((s) => s.trim())
          .filter(Boolean),
        tableListV2: undefined,
      };
    }
    // Unknown object → keep as-is; normalize if it has rows
    if (Array.isArray(raw.rows)) {
      const obj = { version: raw.version || 2, rows: raw.rows };
      return { tableList: obj, tableListV2: obj };
    }
    return { tableList: [], tableListV2: undefined };
  }

  // String case
  const str = String(raw).trim();
  if (!str) return { tableList: [], tableListV2: undefined };

  // If it looks like JSON, try to parse ONCE (do not split by commas!)
  if (
    (str.startsWith("{") && str.endsWith("}")) ||
    (str.startsWith("[") && str.endsWith("]"))
  ) {
    try {
      const parsed = JSON.parse(str);
      if (parsed && parsed.version === 2 && Array.isArray(parsed.rows)) {
        return { tableList: parsed, tableListV2: parsed };
      }
      if (Array.isArray(parsed)) {
        return {
          tableList: parsed
            .map(String)
            .map((s) => s.trim())
            .filter(Boolean),
          tableListV2: undefined,
        };
      }
      if (parsed && Array.isArray(parsed.rows)) {
        const obj = { version: parsed.version || 2, rows: parsed.rows };
        return { tableList: obj, tableListV2: obj };
      }
    } catch {
      // fall through to legacy handling
    }
  }

  // Legacy: split by newline ONLY (do NOT split by commas — breaks JSON)
  const lines = str.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  return { tableList: lines, tableListV2: undefined };
}

/** --- Provider ---------------------------------------------------------- */

export function ConfigProvider({ children }) {
  const [cvReloadTick, setCvReloadTick] = useState(cvReloadInitial);
  const refreshCVs = useCallback(
    () => setCvReloadTick((t) => t + 1),
    [setCvReloadTick]
  );
  const [cvDebug, setCvDebug] = useState(null);
  const [customValues, setCustomValues] = useState([]);
  const [customValuesLoading, setCustomValuesLoading] = useState(false);
  const location = useLocation();

  // Boot from localStorage (versioned) → defaults
  const [config, setConfig] = useState(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    try {
      const saved = JSON.parse(raw);
      if (saved.__v !== STORAGE_VERSION) {
        return { ...DEFAULTS, ...saved, __v: STORAGE_VERSION };
      }
      return { ...DEFAULTS, ...saved };
    } catch {
      return { ...DEFAULTS };
    }
  });

  const setConfigPartial = useCallback((patchOrFn) => {
    setConfig((prev) => {
      const patch =
        typeof patchOrFn === "function" ? patchOrFn(prev) : patchOrFn;
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, []);

  // Merge allowlisted URL params whenever query changes
  useEffect(() => {
    const urlCfg = parseQueryAllowlisted(location.search);
    if (!Object.keys(urlCfg).length) return;
    setConfig((prev) => {
      const merged = coerceTypes({ ...prev, ...urlCfg });
      const changed = Object.keys(urlCfg).some((key) => prev[key] !== merged[key]);
      if (!changed) return prev;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      } catch {}
      return merged;
    });
  }, [location.search]);

  // If no locationId, try env var first, then /public/env
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      if (config.locationId) return;

      // Check VITE_LOCATION_ID env var
      const envLocationId = import.meta.env.VITE_LOCATION_ID || "";
      if (envLocationId) {
        if (!cancelled) {
          setConfigPartial({ locationId: envLocationId, _fetchedCV: false });
        }
        return;
      }

      // Fallback: try /public/env (GHL iframe mode)
      try {
        const res = await fetch("/public/env");
        if (!res.ok) return;
        const json = await res.json();
        if (json?.locationId && !cancelled) {
          setConfigPartial({
            locationId: String(json.locationId),
            _fetchedCV: false,
          });
        }
      } catch {}
    }
    boot();
    return () => {
      cancelled = true;
    };
  }, [config.locationId, setConfigPartial]);

  // Optional headers helper (kept for any remaining direct LC calls)
  const getApiHeaders = useCallback(
    () => {
      const token = config.apiKey || getAuthToken();
      return {
        ...DEFAULT_API_CONFIG.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Version: DEFAULT_API_CONFIG.version,
      };
    },
    [config.apiKey]
  );

  // Fetch Custom Values when we have a locationId
  useEffect(() => {
    if (!config.locationId) return;

    let cancelled = false;

    (async function run() {
      if (!cancelled) setCustomValuesLoading(true);
      try {
        // NEW: Try DynamoDB config first (Sprint 4)
        const API_BASE = import.meta.env.VITE_EDGE_ORIGIN || "";
        if (API_BASE && config.locationId) {
          try {
            const res = await fetch(`${API_BASE}/api/config/${config.locationId}`, {
              headers: { Authorization: `Bearer ${getAuthToken()}` },
            });
            if (res.ok) {
              const dbConfig = await res.json();
              if (dbConfig && (dbConfig.locationId || dbConfig.integrationMode)) {
                // DynamoDB config loaded — use it directly, skip CV parsing
                if (!cancelled) {
                  setConfigPartial(prev => ({
                    ...prev,
                    ...dbConfig,
                    _fetchedCV: true,
                  }));
                  setCustomValuesLoading(false);
                }
                return; // Skip the GHL Custom Values path below
              }
            }
          } catch {
            // DynamoDB config not available — fall through to GHL Custom Values
          }
        }

        // EXISTING: GHL Custom Values path (unchanged)
        // 1) Pull CVs (with auth headers) and normalize to a simple map
        const cvResp =
          (await listAllCustomValues(config.locationId, {
            headers: getApiHeaders(),
            caller: "ConfigProvider",
          }).catch(() => ({}))) || {};
        if (!cancelled) setCustomValues(cvResp?.customValues || []);
        const cvMap = toCvMap(cvResp);
        setCvDebug(cvMap);

        if (!cancelled) {
          // 2) Apply overrides using functional state update (no stale `config`)
          setConfigPartial((prev) => {
            const next = { ...prev };

            // 2a) Simple string fields (skip ones we parse below)
            for (const [cfgKey, cvKey] of Object.entries(CV_TO_CFG)) {
              if (
                [
                  "mealTimes",
                  "oppCFMap",
                  "contactCFMap",
                  "avgCover",
                  "dynamicFields",
                  "pacingCaps",
                  "walkInDefaultDuration",
                  "newBookingDefaultDuration",
                  "newBookingForm",
                  "tableList",
                ].includes(cfgKey)
              ) {
                continue; // handled below
              }
              const v = cvMap[cvKey];
              if (v !== undefined && String(v).trim() !== "") {
                next[cfgKey] = String(v).trim();
              }
            }

            // 2b) Feature flags
            next.featureFlags = String(
              cvMap[CV_TO_CFG.featureFlags] ??
                next.featureFlags ??
                "reservations,month,settings"
            ).trim();

            // 2c) Dynamic fields (newline-separated)
            {
              const dynRaw = cvMap[CV_TO_CFG.dynamicFields];
              if (typeof dynRaw === "string") {
                next.dynamicFields = dynRaw
                  .split(/\r?\n/)
                  .map((s) => s.trim())
                  .filter(Boolean);
              }
            }

            // 2d) Table list — robust parsing (v2 JSON OR legacy)
            {
              const tlRaw = cvMap[CV_TO_CFG.tableList];
              if (tlRaw !== undefined) {
                const { tableList, tableListV2 } = parseTableListCV(tlRaw);
                next.tableList = tableList; // can be v2 object or legacy array
                next.tableListV2 = tableListV2; // when present, always v2 object
              }
            }

            // 2e) Meal times (JSON)
            try {
              const mtRaw = cvMap[CV_TO_CFG.mealTimes];
              if (mtRaw) {
                const obj =
                  typeof mtRaw === "string" ? JSON.parse(mtRaw) : mtRaw;
                if (obj && typeof obj === "object") next.mealTimes = obj;
              }
            } catch (e) {
              console.warn("Invalid meal_times JSON:", e);
            }

            // 2f) Avg cover (number)
            {
              const raw = cvMap[CV_TO_CFG.avgCover];
              if (
                raw !== undefined &&
                raw !== null &&
                String(raw).trim() !== ""
              ) {
                const n = parseFloat(String(raw).trim());
                if (Number.isFinite(n)) {
                  next.avgCover = Math.max(0, Math.min(50, Math.round(n)));
                }
              }
            }

            // 2g) Slot size minutes (number)
            {
              const raw = cvMap[CV_TO_CFG.slotSizeMin];
              const n = Number(raw);
              if (!Number.isNaN(n) && n > 0) next.slotSizeMin = n;
            }

            // 2h) Opportunity CF map (JSON → object)
            try {
              const mapRaw = cvMap[CV_TO_CFG.oppCFMap];
              if (mapRaw) {
                const parsed =
                  typeof mapRaw === "string" ? JSON.parse(mapRaw) : mapRaw;
                if (parsed && typeof parsed === "object")
                  next.oppCFMap = parsed;
              }
            } catch (e) {
              console.warn("Invalid opp_cf_map JSON:", e);
            }

            // 2h.1) Contact CF map (JSON → object)
            try {
              const cMapRaw = cvMap[CV_TO_CFG.contactCFMap];
              if (cMapRaw) {
                const parsed =
                  typeof cMapRaw === "string" ? JSON.parse(cMapRaw) : cMapRaw;
                if (parsed && typeof parsed === "object") {
                  next.contactCFMap = parsed;
                }
              }
            } catch (e) {
              console.warn("Invalid contactcfmap JSON:", e);
            }

            // 2i) Pacing caps (JSON)
            try {
              const pcRaw = cvMap[CV_TO_CFG.pacingCaps];
              if (pcRaw) {
                const obj =
                  typeof pcRaw === "string" ? JSON.parse(pcRaw) : pcRaw;
                if (obj && typeof obj === "object") next.pacingCaps = obj;
              }
            } catch {}

            // 2j) NEW: New Booking Form config (JSON)
            {
              const raw = cvMap[CV_TO_CFG.newBookingForm];
              next._usingDefaultBookingForm = true; // assume default unless valid JSON is present
              if (raw && String(raw).trim() !== "") {
                try {
                  const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
                  if (
                    obj &&
                    typeof obj === "object" &&
                    Array.isArray(obj.fields)
                  ) {
                    next.newBookingForm = obj;
                    next._usingDefaultBookingForm = false;
                  }
                } catch (e) {
                  console.warn("Invalid new_booking_form_config JSON:", e);
                }
              }
            }

            // 2k) NEW: default durations (numbers)
            {
              let usingDefault = false;

              const walkInRaw = cvMap[CV_TO_CFG.walkInDefaultDuration];
              if (
                walkInRaw !== undefined &&
                walkInRaw !== null &&
                String(walkInRaw).trim() !== ""
              ) {
                const n = parseInt(String(walkInRaw).trim(), 10);
                if (Number.isFinite(n) && n > 0)
                  next.walkInDefaultDuration = n;
              } else {
                usingDefault = true;
              }

              const nbRaw = cvMap[CV_TO_CFG.newBookingDefaultDuration];
              if (
                nbRaw !== undefined &&
                nbRaw !== null &&
                String(nbRaw).trim() !== ""
              ) {
                const n = parseInt(String(nbRaw).trim(), 10);
                if (Number.isFinite(n) && n > 0)
                  next.newBookingDefaultDuration = n;
              } else {
                usingDefault = true;
              }

              next._usingDefaultDurations = usingDefault;
            }

            next._fetchedCV = true;
            return next;
          });
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setConfigPartial({ _fetchedCV: true });
      } finally {
        if (!cancelled) setCustomValuesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Include getApiHeaders + setConfigPartial in deps since we call them here
  }, [config.locationId, cvReloadTick, getApiHeaders, setConfigPartial]);

  const customValuesByFieldKey = useMemo(() => {
    const map = new Map();
    for (const cv of customValues) map.set(cv.fieldKey, cv);
    return map;
  }, [customValues]);

  const featureFlagsSet = useMemo(
    () => parseFeatureFlagsToSet(config.featureFlags),
    [config.featureFlags]
  );

  const inferredLocationId = useMemo(() => initialLocationId(), []);

  const { ok: configOk, errors: configErrors, normalized } = useMemo(
    () => validateRuntimeConfig(config),
    [config]
  );

  const needsOnboarding = useMemo(() => {
    // Don't trigger until config has been fetched (avoid flash of wizard on load)
    if (!config._fetchedCV) return false;

    // Standalone locations: check if onboarding was completed
    if (config.integrationMode === 'standalone') {
      return !config.onboardingComplete;
    }

    // GHL locations: check if critical CVs are present
    const hasCalendar = !!config.calendarId;
    const hasPipeline = !!config.pipelineId;
    const mt = config.mealTimes;
    const hasMealTimes = mt && typeof mt === "object" &&
      (Array.isArray(mt) ? mt.length > 0 : !!(mt.breakfast || mt.lunch || mt.dinner));

    return !hasCalendar || !hasPipeline || !hasMealTimes;
  }, [config]);

  const value = useMemo(
    () => ({
      config: normalized,
      configOk,
      configErrors,
      cvDebug,
      needsOnboarding,
      setConfig: setConfigPartial,
      getApiHeaders,
      featureFlagsSet,
      initialLocationId, // fn for debugging if needed
      inferredLocationId,
      refreshCVs,
      cvReloadTick,
      customValues,
      customValuesByFieldKey,
      customValuesLoading,
    }),
    [
      normalized,
      configOk,
      configErrors,
      cvDebug,
      needsOnboarding,
      setConfigPartial,
      getApiHeaders,
      featureFlagsSet,
      inferredLocationId,
      refreshCVs,
      cvReloadTick,
      customValues,
      customValuesByFieldKey,
      customValuesLoading,
    ]
  );

  if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
    window.__yebo = value;
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Hook */
export function useConfig() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useConfig must be used within ConfigProvider");
  return ctx;
}
